import {
  LuLayoutDashboard,
  LuCalendarDays,
  LuUsers,
  LuUser,
  LuTicket,
  LuPackage,
  LuMail,
  LuChevronLeft,
  LuLogOut,
  LuMenu,
  LuX,
  LuBus,
  LuBuilding2
} from "react-icons/lu";
import { FiHome } from "react-icons/fi";
import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getAuth, signOut } from "firebase/auth";

export const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const auth = getAuth();

  useEffect(() => {
    // Force reset body styles for full-screen admin layout
    const originalPadding = document.body.style.paddingTop;
    const originalOverflow = document.body.style.overflow;
    
    document.body.style.paddingTop = '0px';
    document.body.style.overflow = 'hidden'; // Prevent body scroll, handle inside layout
    
    return () => {
      document.body.style.paddingTop = originalPadding || '40px'; 
      document.body.style.overflow = originalOverflow || 'auto';
    };
  }, []);

  // Removed Strict Redirect - allowing navigation but blurring content

  if (loading) return <div className="p-4 text-center">Loading...</div>;

  // RPC officers may enter the admin shell, but only for Racepack.
  if (!user || !["admin", "superadmin", "rpc"].includes(user.role)) {
      return <Navigate to="/" replace />;
  }

  if (user.role === "rpc" && location.pathname !== "/admin/racepack") {
    return <Navigate to="/admin/racepack" replace />;
  }


  const allMenuItems = [
    { name: "Dashboard", path: "/admin", icon: LuLayoutDashboard },
    { name: "Events", path: "/admin/event", icon: LuCalendarDays },
    { name: "Participants", path: "/admin/participants", icon: LuUsers },
    { name: "Shuttle", path: "/admin/shuttle", icon: LuBus },
    { name: "Hotel", path: "/admin/hotel", icon: LuBuilding2 },
    { name: "Vouchers", path: "/admin/vouchers", icon: LuTicket },
    { name: "Racepack", path: "/admin/racepack", icon: LuPackage },
    { name: "Emails", path: "/admin/emails", icon: LuMail },
  ];

  const menuItems = user?.role === "rpc"
    ? allMenuItems.filter((item) => item.path === "/admin/racepack")
    : user?.role === "admin"
    ? allMenuItems.filter((item) => item.path === "/admin/participants")
    : allMenuItems;

  // Determine if content should be blurred
  // Admin is allowed on /admin/participants, /admin/users AND their sub-routes
  const isRestrictedPath = user.role === 'admin' && 
                           !location.pathname.startsWith('/admin/participants');

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (user.role === "rpc") {
    return (
      <div className="fixed inset-0 bg-slate-50 font-sans overflow-hidden">
        <main className="h-full overflow-auto p-4">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex bg-slate-50 font-sans overflow-hidden">
      {/* Mobile Backdrop */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 z-[110] md:hidden transition-opacity"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-[120] bg-white border-r border-slate-200 transition-all duration-300 ease-in-out flex flex-col
          ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          ${sidebarOpen ? "md:w-56" : "md:w-16"}
          w-64 md:relative
        `}
      >
        {/* Header */}
        <div className="flex h-14 items-center justify-between px-4 border-b border-slate-100">
          {(sidebarOpen || mobileMenuOpen) && (
            <span className="text-lg font-semibold text-slate-800">
              {user.role === "rpc" ? "RPC Panel" : "Admin Panel"}
            </span>
          )}
          <button
            onClick={() => mobileMenuOpen ? setMobileMenuOpen(false) : setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            {mobileMenuOpen ? (
              <LuX className="w-5 h-5" />
            ) : (
              <LuChevronLeft 
                className={`w-4 h-4 transition-transform duration-200 ${!sidebarOpen ? "rotate-180" : ""}`} 
              />
            )}
          </button>
        </div>

        {/* Menu */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={!sidebarOpen ? item.name : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-base transition-colors ${
                  location.pathname === item.path
                    ? "bg-slate-100 text-slate-900 font-medium"
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                }`}
              >
                <Icon 
                  className={`w-5 h-5 flex-shrink-0 ${location.pathname === item.path ? "text-slate-700" : "text-slate-400"}`} 
                />
                {(sidebarOpen || mobileMenuOpen) && <span>{item.name}</span>}
              </Link>
          )})}
        </nav>

        {/* Back to Home is intentionally hidden for RPC-only accounts. */}
        {user.role !== "rpc" && (
          <div className="p-2 border-t border-slate-100">
            <Link
              to="/"
              title={!sidebarOpen ? "Home" : undefined}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-base text-slate-500 hover:bg-slate-50 transition-colors"
            >
              <FiHome className="w-5 h-5 flex-shrink-0" />
              {(sidebarOpen || mobileMenuOpen) && <span>Home</span>}
            </Link>
          </div>
        )}
      </aside>

      {/* Main Content Container */}
      <div className="flex-1 flex flex-col min-w-0 bg-slate-50 relative overflow-hidden">
        {/* Mobile Top Navbar */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 md:hidden flex-shrink-0">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 -ml-2 rounded-xl text-slate-600 hover:bg-slate-100"
          >
            <LuMenu className="w-6 h-6" />
          </button>
          <span className="ml-3 font-bold text-slate-800">
            {user.role === "rpc" ? "RPC" : "Admin"}
          </span>
        </header>

        {/* Content Body */}
        <main className={`flex-1 overflow-auto p-4 transition-all duration-300 ${
            isRestrictedPath ? "blur-sm opacity-50 pointer-events-none select-none overflow-hidden" : ""
        }`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};
